import { Helmet } from 'react-helmet';
import { useStore } from '../../../../context/StoreContext';

/* LINK-PREVIEW TAGS for a shared storefront URL.

   iMessage draws the large card only from a large `og:image`; a logo gets the
   small grey bubble. So every page passes the most shoppable photo it has (a
   product shot, a banner) and the store logo is only the last resort.

   These are written by the browser, so only previewers that run JavaScript
   (iMessage) see them. Slack, WhatsApp and Facebook read the raw HTML and need
   the tags injected server-side. */
const ShareMeta = ({ title, description, image }) => {
    const { storeDetails } = useStore() || {};
    const storeName = storeDetails?.store_name;
    const img = image || storeDetails?.store_logo;
    const desc = description || (storeName ? `Shop ${storeName}` : '');

    return (
        <Helmet>
            {title && <meta property="og:title" content={title} />}
            {desc && <meta property="og:description" content={desc} />}
            {img && <meta property="og:image" content={img} />}
            {storeName && <meta property="og:site_name" content={storeName} />}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={window.location.href} />
            <meta name="twitter:card" content={img ? 'summary_large_image' : 'summary'} />
        </Helmet>
    );
};

/* The main photo of a product as the API ships it: `is_main` first, else the first. */
export const mainImageOf = (product) => {
    const images = Array.isArray(product?.product_images) ? product.product_images : [];
    return [...images].sort((a, b) => Number(b.is_main) - Number(a.is_main))[0]?.src;
};

/* First product in a list that has a photo. */
export const firstImageOf = (products) => (products || []).map(mainImageOf).find(Boolean);

/* A collection's cover. `logo` is the cover column on store_collections;
   `image`/`cover` are read too, as `normalizeCollection` does in
   collectionsMarkup.js. */
const coverOf = (collection) => String(collection?.logo || collection?.image || collection?.cover || '').trim();

/* The first cover across the collections, else the first collection's first
   product photo. */
export const collectionImageOf = (collections) => (
    (collections || []).map(coverOf).find(Boolean)
    || firstImageOf(collections?.[0]?.products)
);

export default ShareMeta;
